import { create } from 'zustand';
import type { SubscriberCountsDto, SubscriberDto, SubscriberStatus } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { saveFile } from '@/lib/download';

/** The newsletter's list (ADR-099) — searched, filtered and paged on the server. */
interface SubscribersState {
  items: SubscriberDto[];
  counts: SubscriberCountsDto | null;
  nextCursor: string | null;
  search: string;
  status: SubscriberStatus | null;
  loading: boolean;
  exporting: boolean;
  error: string | null;

  load: (filters?: { search?: string; status?: SubscriberStatus | null }) => Promise<void>;
  loadMore: () => Promise<void>;
  exportCsv: () => Promise<void>;
  withdraw: (id: string) => Promise<void>;
}

const PAGE_SIZE = 20;
let listRequest = 0;

export const useSubscribersStore = create<SubscribersState>((set, get) => ({
  items: [],
  counts: null,
  nextCursor: null,
  search: '',
  status: null,
  loading: false,
  exporting: false,
  error: null,

  async load(filters = {}) {
    const search = filters.search ?? get().search;
    const status = filters.status === undefined ? get().status : filters.status;
    const request = ++listRequest;
    set({ loading: true, error: null, search, status });
    try {
      const [page, counts] = await Promise.all([
        withSession(() => adminApi.subscribers.list({ limit: PAGE_SIZE, ...(search.trim() ? { search: search.trim() } : {}), ...(status ? { status } : {}) })),
        withSession(() => adminApi.subscribers.counts()),
      ]);
      if (request !== listRequest) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, counts, loading: false });
    } catch (error) {
      if (request !== listRequest) return;
      set({ loading: false, error: errorMessage(error, 'Could not load the subscribers.') });
    }
  },

  async loadMore() {
    const { nextCursor, loading, search, status } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.subscribers.list({ limit: PAGE_SIZE, cursor: nextCursor, ...(search.trim() ? { search: search.trim() } : {}), ...(status ? { status } : {}) }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more subscribers.') });
    }
  },

  async exportCsv() {
    const { status } = get();
    set({ exporting: true, error: null });
    try {
      await saveFile(() => withSession(() => adminApi.subscribers.exportCsv(status ?? undefined)), `subscribers-${status ?? 'all'}.csv`);
    } catch (error) {
      set({ error: errorMessage(error, 'Could not export the list.') });
    } finally {
      set({ exporting: false });
    }
  },

  async withdraw(id) {
    try {
      const updated = await withSession(() => adminApi.subscribers.withdraw(id));
      set((state) => ({ items: state.items.map((item) => (item.id === id ? updated : item)) }));
      void get().load();
    } catch (error) {
      set({ error: errorMessage(error, 'Could not withdraw this address.') });
    }
  },
}));
